import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { leadsApi, sitesApi } from "../lib/api";
import { useSortableTable } from "../hooks/useSortableTable";
import { SortHeader } from "../components/SortHeader";
import { TrustPill } from "../components/TrustPill";

export function LeadsPage() {
  const [params, setParams] = useSearchParams();
  const siteFilter = params.get("siteId") ?? "";
  const setSiteFilter = (id: string) => {
    const next = new URLSearchParams(params);
    if (id) next.set("siteId", id); else next.delete("siteId");
    setParams(next, { replace: true });
  };
  const [minScore, setMinScore] = useState(0);

  const sitesQ = useQuery({ queryKey: ["sites"], queryFn: sitesApi.list });
  const leadsQ = useQuery({
    queryKey: ["leads", siteFilter, minScore],
    queryFn: () => leadsApi.list({
      siteId: siteFilter || undefined,
      minScore: minScore > 0 ? minScore : undefined,
    }),
    refetchInterval: 30_000,
  });

  type LeadRow = NonNullable<typeof leadsQ.data>["leads"][number];
  const { sortedRows, sortKey, sortDir, toggleSort } = useSortableTable<LeadRow>(
    leadsQ.data?.leads ?? [],
    {
      received: (l) => l.receivedAt,
      site: (l) => l.siteDomain ?? l.siteId,
      caller: (l) => l.callerName,
      phone: (l) => l.callerPhone,
      service: (l) => l.message,
      score: (l) => l.qualityScore,
    },
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-bold flex items-center gap-2">
            Leads <TrustPill label={leadsQ.data?.dataIntegrity?.leads} size="md" />
          </h1>
          <p className="text-ink-600 text-sm mt-1">
            Form submissions and tracked calls across the portfolio.
            {leadsQ.data?.showTests && (
              <span className="ml-2 text-amber-700 font-medium">Audit view (?showTests=true) — test/sentinel rows included.</span>
            )}
            {leadsQ.data?.includeArchived && (
              <span className="ml-2 text-amber-700 font-medium">Includes archived/paused-site leads.</span>
            )}
          </p>
        </div>
        {leadsQ.data && (
          <div className="text-right">
            <div className="text-2xl font-display font-bold">{leadsQ.data.leads.length}</div>
            <div className="text-xs uppercase tracking-wider text-ink-500">shown</div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-xl border border-ink-100 p-4 mb-4 flex flex-wrap gap-3 items-end">
        <Field label="Site">
          <select
            value={siteFilter}
            onChange={(e) => setSiteFilter(e.target.value)}
            className="px-3 py-2 border border-ink-200 rounded-md text-sm bg-white min-w-[16rem]"
          >
            <option value="">All sites</option>
            {sitesQ.data?.sites.map((s) => (
              <option key={s.id} value={s.id}>{s.domain}</option>
            ))}
          </select>
        </Field>
        <Field label="Min score">
          <select
            value={minScore}
            onChange={(e) => setMinScore(parseInt(e.target.value, 10))}
            className="px-3 py-2 border border-ink-200 rounded-md text-sm bg-white"
          >
            <option value={0}>Any</option>
            <option value={25}>25+ (non-spam)</option>
            <option value={50}>50+ (alerted)</option>
            <option value={75}>75+ (high quality)</option>
          </select>
        </Field>
      </div>

      {leadsQ.isLoading && <Loading />}
      {leadsQ.error && <ErrorBox msg={String((leadsQ.error as Error).message)} />}
      {leadsQ.data && (
        leadsQ.data.leads.length === 0 ? <EmptyState /> : (
          <div className="bg-white rounded-xl border border-ink-100 overflow-hidden shadow-sm">
            <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead className="bg-ink-50 text-xs uppercase tracking-wider text-ink-500">
                <tr>
                  <SortHeader label="Received" sortKey="received" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Site" sortKey="site" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Caller" sortKey="caller" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Phone" sortKey="phone" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Service / message" sortKey="service" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-left px-4 py-3" />
                  <SortHeader label="Score" sortKey="score" currentSortKey={sortKey} currentDir={sortDir} onSort={toggleSort} className="text-right px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-100">
                {sortedRows.map((l) => (
                  <tr key={l.id} className={l.isSpam ? "opacity-50 hover:bg-ink-50/50" : "hover:bg-ink-50/50"}>
                    <td className="px-4 py-3 text-ink-600 whitespace-nowrap">
                      {timeAgo(l.receivedAt)}
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-ink-700 font-medium">{l.siteDomain ?? l.siteId.slice(0, 8) + "…"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>{l.callerName ?? "—"}</div>
                      {l.callerEmail && <div className="text-xs text-ink-500">{l.callerEmail}</div>}
                    </td>
                    <td className="px-4 py-3">
                      {l.callerPhone ? (
                        <a href={`tel:${l.callerPhone}`} className="text-ink-900 hover:text-accent">
                          {formatPhone(l.callerPhone)}
                        </a>
                      ) : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-ink-600 max-w-md truncate">
                      {l.message ?? <span className="text-ink-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <ScoreBadge score={l.qualityScore ?? 0} isSpam={l.isSpam} type={l.type} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs uppercase tracking-wider text-ink-500 font-medium">{label}</span>
      {children}
    </label>
  );
}

function ScoreBadge({ score, isSpam, type }: { score: number; isSpam: boolean; type: string }) {
  if (isSpam) {
    return <span className="inline-flex px-2 py-0.5 text-xs rounded-md font-medium bg-ink-100 text-ink-500">spam · {score}</span>;
  }
  let tone = "bg-ink-100 text-ink-700";
  if (score >= 75) tone = "bg-green-100 text-green-800";
  else if (score >= 50) tone = "bg-accent-100 text-accent-700";
  else if (score >= 25) tone = "bg-blue-100 text-blue-800";
  return (
    <span className={`inline-flex px-2 py-0.5 text-xs rounded-md font-bold ${tone}`}>
      {type === "call" ? "📞" : ""} {score}
    </span>
  );
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

function formatPhone(p: string): string {
  const digits = p.replace(/\D/g, "");
  if (digits.length === 10) return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === "1") return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  return p;
}

function Loading() {
  return <div className="bg-white rounded-xl border border-ink-100 p-12 text-center text-ink-500">Loading…</div>;
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4">
      <strong>Failed to load leads.</strong> {msg}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="bg-white rounded-xl border border-ink-100 p-12 text-center">
      <p className="text-ink-700 font-medium">No leads match these filters.</p>
      <p className="text-ink-500 text-sm mt-1">
        Try widening the score range, or wait — leads land here within seconds of submission.
      </p>
    </div>
  );
}
